import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBarBody,
  MessageBarTitle,
  Spinner
} from '@fluentui/react-components';
import { Warning16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { PriceDetails } from '../../../models/Entities/PriceList/PriceDetail';

// guía
import { GuideTable, GuideTh, GuideRowActions, guideCellClassName as cell, guideRowClassName, useGuideSort } from '../../../theme/components/GuideTable';
import { GuidePagination, useGuidePagination } from '../../../theme/components/GuidePagination';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideCountChip } from '../../../theme/components/GuideKpiCard';
import { GuideTooltip } from '../../../theme/components/GuideTooltip';

// lógica
import { changedPricesCount, enrichPrices, formatPrice, parsePriceInput } from '../PriceList.Logic';

import * as ComponentFunctions from './PriceListComponentFunctions';
import './PricesList.css';

const PAGE_SIZES = [25, 50, 100];

type PriceField = 'price' | 'remotePrice' | 'preScreeningPrice' | 'randomPrice' | 'failPrice' | 'additionalPaymentPrice';

interface PriceColumn {
  key: PriceField;
  labelKey: string;
  change: (price: PriceDetails) => void;
}

/**
 * Grilla de precios de la lista elegida (guía): encabezados en mayúsculas ordenables, importes a
 * la derecha en tabulares con la moneda en secundario, filas modificadas en tono "pendiente"
 * durante la edición masiva, acciones en ⋯ (Editar, Eliminar con confirmación) y paginación.
 */
export const PricesListComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();
  const [deleting, setDeleting] = useState<PriceDetails | undefined>(undefined);
  const [deleteRequested, setDeleteRequested] = useState(false);

  // Carga la lista si todavía no está en el store (el padre sólo tiene la asociación).
  useEffect(() => {
    if (!props.PriceList && !props.IsGettingPriceListById) props.GetPriceListById(props.PriceListId);
  }, [props.PriceListId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!props.MedicalServices || props.MedicalServices.length === 0) props.GetAllMedicalServices(1, 1000, undefined);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Al salir de la edición masiva se guardan los precios que quedaron marcados como cambiados.
  useEffect(() => {
    if (props.PriceMassiveEditionIsActive) return;
    props.PriceList?.prices?.forEach((price) => { if (price.isChanged) props.SavePrice(price); });
  }, [props.PriceMassiveEditionIsActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // El diálogo de borrado se cierra solo cuando la operación terminó bien.
  useEffect(() => {
    if (deleteRequested && !props.IsDeletingPrice && !props.FailOnDeletingPrice) {
      setDeleting(undefined);
      setDeleteRequested(false);
    }
  }, [deleteRequested, props.IsDeletingPrice, props.FailOnDeletingPrice]);

  const items = useMemo(
    () => enrichPrices(props.PriceList?.prices, props.MedicalServices, props.ServiceFilterCriteria),
    [props.PriceList?.prices, props.MedicalServices, props.ServiceFilterCriteria]
  );

  const priceColumns: PriceColumn[] = [
    props.showPrice && { key: 'price' as const, labelKey: 'priceList.priceColumn.price', change: (p: PriceDetails) => props.ChangePrice(p) },
    props.showRemotePrice && { key: 'remotePrice' as const, labelKey: 'priceList.priceColumn.remotePrice', change: (p: PriceDetails) => props.ChangeRemotePrice(p) },
    props.showPreScreaningPrice && { key: 'preScreeningPrice' as const, labelKey: 'priceList.priceColumn.preScreeningPrice', change: (p: PriceDetails) => props.ChangePreScreaningPrice(p) },
    props.showRandomPrice && { key: 'randomPrice' as const, labelKey: 'priceList.priceColumn.randomPrice', change: (p: PriceDetails) => props.ChangeRandomPrice(p) },
    props.showFailPrice && { key: 'failPrice' as const, labelKey: 'priceList.priceColumn.failurePrice', change: (p: PriceDetails) => props.ChangeFailPrice(p) },
    props.showAdditionalPaymentPrice && { key: 'additionalPaymentPrice' as const, labelKey: 'priceList.priceColumn.copay', change: (p: PriceDetails) => props.ChangeAdditionalPaymentPrice(p) }
  ].filter(Boolean) as PriceColumn[];

  const accessors: Record<string, (row: PriceDetails) => string | number | undefined> = {
    description: (row) => row.Description,
    currency: (row) => row.currency
  };
  priceColumns.forEach((column) => { accessors[column.key] = (row) => row[column.key] ?? undefined; });

  const { rows, sortProps } = useGuideSort<PriceDetails>(items, accessors, { key: 'description', direction: 'asc' });
  const { pageRows, paginationProps } = useGuidePagination(rows, 50);

  const total = props.PriceList?.prices?.length ?? 0;
  const changed = changedPricesCount(props.PriceList?.prices);
  const isSearching = !!(props.ServiceFilterCriteria && props.ServiceFilterCriteria.trim());
  const loading = !props.PriceList && props.IsGettingPriceListById;
  const loadError = props.FailOnGettingPriceListById || props.FailOnGettingAllPriceList || props.FailOnGettingAllMedicalServices;
  const saveError = props.FailOnUpdatingPrice || props.FailOnDeletingPrice;

  const renderPriceCell = (item: PriceDetails, column: PriceColumn) => {
    if (!props.PriceMassiveEditionIsActive) {
      return (
        <span className="prl-amount">
          <span className="prl-amount__value">{formatPrice(item[column.key])}</span>
          <span className="prl-amount__currency">{item.currency}</span>
        </span>
      );
    }
    return (
      <Input
        key={`${item.id}-${column.key}`}
        className={`prl-input${item.isChanged ? ' is-changed' : ''}`}
        size="small"
        type="text"
        inputMode="decimal"
        defaultValue={formatPrice(item[column.key]).replace(/\./g, '')}
        contentAfter={<span className="prl-amount__currency">{item.currency}</span>}
        aria-label={`${t(column.labelKey)} ${item.Description ?? ''}`}
        onFocus={(event) => event.target.select()}
        onChange={(_event, data) => {
          const value = parsePriceInput(data.value);
          if (value !== undefined && value !== item[column.key]) column.change({ ...item, [column.key]: value });
        }}
      />
    );
  };

  const renderRow = (item: PriceDetails) => (
    <tr key={item.id} className={guideRowClassName({ pending: item.isChanged, inactive: !item.isEnabled })} data-testid={`prl-row-${item.id}`}>
      <td>
        <span className="prl-service">
          <span className="prl-service__name">{item.Description}</span>
          {!item.isEnabled && (
            <GuideTooltip content={t('priceList.medicalPracticeColumn.disabledService')} relationship="description">
              <span className="prl-service__warn" aria-label={t('priceList.medicalPracticeColumn.disabledService')}><Warning16Regular /></span>
            </GuideTooltip>
          )}
        </span>
      </td>
      {priceColumns.map((column) => (
        <td key={column.key} className={cell('num', 'end')}>{renderPriceCell(item, column)}</td>
      ))}
      <td className={cell('actions')}>
        <GuideRowActions
          label={t('priceList.actions.rowActions', { name: item.Description })}
          disabled={props.PriceMassiveEditionIsActive}
          actions={[
            { key: 'edit', label: t('priceList.actions.edit'), onClick: () => props.onEditPrice?.(item) },
            { key: 'delete', label: t('priceList.actions.delete'), danger: true, onClick: () => setDeleting(item) }
          ]}
        />
      </td>
    </tr>
  );

  const renderBody = () => {
    if (loading) return <GuideSkeleton rows={4} label={t('priceList.loading')} data-testid="prl-loading" />;
    if (!props.PriceList) return null;
    if (total === 0) {
      return <GuideEmptyState title={t('medicalSpecialties.priceList.noPrices')} text={t('priceList.empty.text')} data-testid="prl-empty" />;
    }
    if (items.length === 0) {
      return <GuideEmptyState title={t('priceList.noMatch.title')} text={t('priceList.noMatch.text', { search: props.ServiceFilterCriteria })} data-testid="prl-no-match" />;
    }
    return (
      <GuideTable
        ariaLabel={t('priceList.ariaLabel')}
        data-testid="prl-table"
        footer={<GuidePagination {...paginationProps} pageSizeOptions={PAGE_SIZES} />}
      >
        <thead>
          <tr>
            <GuideTh sort={sortProps('description')}>{t('priceList.medicalPracticeColumn.practice')}</GuideTh>
            {priceColumns.map((column) => (
              <GuideTh key={column.key} align="end" sort={sortProps(column.key)}>{t(column.labelKey)}</GuideTh>
            ))}
            <GuideTh className={cell('actions')}><span className="prl-sr-only">{t('priceList.actions.column')}</span></GuideTh>
          </tr>
        </thead>
        <tbody>{pageRows.map(renderRow)}</tbody>
      </GuideTable>
    );
  };

  return (
    <div className="prl" data-testid="prices-list">
      {loadError && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="prl-load-error">
          <MessageBarBody>
            <MessageBarTitle>{t('priceList.errors.load')}</MessageBarTitle>
            {props.PriceListOperationError?.ErrorMessage || props.MedicalServiceOperationError?.ErrorMessage}
          </MessageBarBody>
        </GuideMessageBar>
      )}
      {saveError && !deleting && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="prl-save-error">
          <MessageBarBody>
            <MessageBarTitle>{t('priceList.errors.save')}</MessageBarTitle>
            {props.PriceOperationError?.ErrorMessage}
          </MessageBarBody>
        </GuideMessageBar>
      )}
      {props.PriceMassiveEditionIsActive && (
        <GuideMessageBar intent="info" layout="multiline" data-testid="prl-massive-banner">
          <MessageBarBody>
            <MessageBarTitle>{t('priceList.massive.title')}</MessageBarTitle>
            {t('priceList.massive.hint', { count: changed })}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {props.PriceList && total > 0 && (
        <div className="prl__summary">
          <GuideCountChip label={t('priceList.summary.total')} count={total} />
          {isSearching && <GuideCountChip label={t('priceList.summary.matching')} count={items.length} className="prl__chip--match" />}
          {props.PriceMassiveEditionIsActive && changed > 0 && <GuideCountChip label={t('priceList.summary.changed')} count={changed} className="prl__chip--changed" />}
        </div>
      )}

      {renderBody()}

      <Dialog open={!!deleting} modalType="alert" onOpenChange={(_event, data) => { if (!data.open && !props.IsDeletingPrice) setDeleting(undefined); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('priceList.delete.title')}</DialogTitle>
            <DialogContent>
              <p className="prl__dialog-text">{t('priceList.delete.text', { name: deleting?.Description })}</p>
              {deleteRequested && props.FailOnDeletingPrice && (
                <GuideMessageBar intent="error" layout="multiline">
                  <MessageBarBody>
                    <MessageBarTitle>{t('priceList.errors.save')}</MessageBarTitle>
                    {props.PriceOperationError?.ErrorMessage}
                  </MessageBarBody>
                </GuideMessageBar>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleting(undefined)} disabled={props.IsDeletingPrice}>{t('priceList.delete.cancel')}</Button>
              <Button
                appearance="primary"
                className="prl__danger"
                disabled={props.IsDeletingPrice}
                icon={props.IsDeletingPrice ? <Spinner size="extra-tiny" /> : undefined}
                onClick={() => { if (deleting) { setDeleteRequested(true); props.DeletePrice(deleting); } }}
                data-testid="prl-delete-confirm"
              >
                {t('priceList.delete.confirm')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(PricesListComponent as any);
